// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { findRestaurant } from "../services/restaurantFinderCore"; // rebuilt core finder
import AttributionFooter from "./AttributionFooter";
import { ConfidencePill } from "./ConfidencePill";
import { StepBar } from "./StepBar";
import { EvidenceChips } from "./EvidenceChips";
import { GoogleMap, Marker } from '@react-google-maps/api';
import { getGoogle } from '../lib/maps/placesClient';
import { ChevronDown, ChevronUp, MapPin, Upload, AlertCircle, Clock } from 'lucide-react';
import StarRating from './StarRating';
type Result = { 
  placeId: string; 
  name: string; 
  address: string; 
  confidence: number; 
  reasons: string[]; 
  phone?: string;
  rating?: number;
  cuisine?: string;
  openNow?: boolean;
  hours?: string[];
  priceLevel?: number;
};

function percent(n: number) {
  if (!Number.isFinite(n)) return "0%";
  const p = Math.round(Math.max(0, Math.min(1, n)) * 100);
  return `${p}%`;
}

function classNames(...xs: Array<string | false | null | undefined>) {
  return xs.filter(Boolean).join(" ");
}

// URL-only utilities
const toTel = (raw?: string) => (raw ? raw.trim().replace(/[^\d+]/g, "") : "");
const SUPPORTED = new Set([
  "instagram.com","www.instagram.com",
  "tiktok.com","www.tiktok.com",
  "facebook.com","www.facebook.com",
]);

export default function FindRestaurantCard() {
  const [mediaUrl, setMediaUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [hashtags, setHashtags] = useState("");
  const [cityHint, setCityHint] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [corsBlocked, setCorsBlocked] = useState(false);
  const [results, setResults] = useState<Result[]>([]);
  const [ran, setRan] = useState(false);
  const [step, setStep] = useState(0);
  const [urlError, setUrlError] = useState<string | null>(null);
  
  // Map state
  const [mapCollapsed, setMapCollapsed] = useState(true);
  const [selectedResultIdx, setSelectedResultIdx] = useState<number | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>({ lat: 0, lng: 0 });
  const [mapZoom, setMapZoom] = useState(2);
  const [markers, setMarkers] = useState<Array<{ position: google.maps.LatLngLiteral; result: Result }>>([]);
  const abortRef = useRef<AbortController | null>(null);

  const host = useMemo(() => {
    try { return new URL(mediaUrl).hostname; } catch { return ""; }
  }, [mediaUrl]);
  const isSupportedHost = host ? SUPPORTED.has(host) : true;

  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit = useCallback(async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const trimmed = mediaUrl.trim();
    if (!trimmed) { setUrlError("Enter a URL."); return; }
    try { new URL(trimmed); setUrlError(null); }
    catch { setUrlError("Enter a valid URL."); return; }
    setError(null);
    setCorsBlocked(false);
    setLoading(true);
    setRan(false);
    setResults([]);
    setStep(0);
    // Deterministic progress – 5 steps @ 160ms
    const timer = setInterval(() => setStep((s) => (s < 5 ? s + 1 : 5)), 160);
    try {
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      const tags = hashtags.split(/[\,\s]+/).map((t) => t.trim()).filter(Boolean);
      const out = await findRestaurant({
        mediaUrl: trimmed,
        caption: caption.trim() || undefined,
        hashtags: tags.length ? tags : undefined,
        cityHint: cityHint.trim() || undefined,
      }, { signal: abortRef.current!.signal });
      setResults(out);
      setRan(true);
    } catch (err: any) {
      const errMsg = err?.message || "Something went wrong.";
      // Detect CORS-related errors
      if (errMsg.includes('CORS') || errMsg.includes('cross-origin') || errMsg.includes('tainted')) {
        setCorsBlocked(true);
      } else {
        setError(errMsg);
      }
    } finally {
      clearInterval(timer);
      setStep(5);
      setLoading(false);
    }
  }, [mediaUrl, caption, hashtags, cityHint]);

  const top = useMemo(() => results[0], [results]);
  const showDebug = (process.env.REACT_APP_DEBUG_PIPELINE === '1') || (process.env.NODE_ENV === 'development');

  // Initialize Google Maps
  useEffect(() => {
    getGoogle()
      .then(() => setMapReady(true))
      .catch(() => setMapReady(false));
  }, []);

  // Update map markers when results change
  useEffect(() => {
    if (!mapReady || !results.length) {
      setMarkers([]);
      return;
    }

    const geocoder = new google.maps.Geocoder();
    const newMarkers: Array<{ position: google.maps.LatLngLiteral; result: Result }> = [];
    let processed = 0;

    const bounds = new google.maps.LatLngBounds();
    let hasBounds = false;

    results.forEach((result, idx) => {
      geocoder.geocode({ address: result.address }, (geoResults, status) => {
        processed++;
        
        if (status === 'OK' && geoResults && geoResults[0]) {
          const location = geoResults[0].geometry.location;
          const position = { lat: location.lat(), lng: location.lng() };
          newMarkers.push({ position, result });
          bounds.extend(location);
          hasBounds = true;
        }

        // When all processed, update state
        if (processed === results.length) {
          setMarkers(newMarkers);
          
          if (hasBounds && newMarkers.length > 0) {
            if (newMarkers.length === 1) {
              setMapCenter(newMarkers[0].position);
              setMapZoom(15);
            } else {
              const center = bounds.getCenter();
              setMapCenter({ lat: center.lat(), lng: center.lng() });
              setMapZoom(12);
            }
            
            // Auto-expand map on mobile if we have results
            if (window.innerWidth <= 768) {
              setMapCollapsed(false);
            }
          }
        }
      });
    });
  }, [results, mapReady]);

  // Handle marker click to select result
  const handleMarkerClick = useCallback((result: Result) => {
    const idx = results.findIndex(r => r.placeId === result.placeId);
    setSelectedResultIdx(idx);
  }, [results]);

  // Handle result item click to center map
  const handleResultClick = useCallback((idx: number) => {
    setSelectedResultIdx(idx);
    const marker = markers.find(m => m.result.placeId === results[idx]?.placeId);
    if (marker) {
      setMapCenter(marker.position);
      setMapZoom(16);
      // Ensure map is visible on mobile
      if (window.innerWidth <= 768 && mapCollapsed) {
        setMapCollapsed(false);
      }
    }
  }, [markers, results, mapCollapsed]);

  return (
    <div className="max-w-3xl mx-auto p-4">
      <div className="rounded-2xl shadow-lg border bg-white">
        <div className="p-5 border-b">
          <h2 className="text-2xl font-semibold">Find Restaurant from Reel</h2>
          <p className="text-sm text-gray-600 mt-1">
            Paste a reel/video URL and optional clues. We’ll resolve to a canonical Google <code>place_id</code> and explain why.
          </p>
  {showDebug && (
    <div className="mt-2 inline-flex items-center gap-2 text-xs text-purple-800 bg-purple-100 border border-purple-200 rounded px-2 py-0.5">
      Dev debug enabled
    </div>
  )}
        </div>

        <form ref={formRef} onSubmit={onSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium">Media URL</label>
            <input
              type="url"
              placeholder="https://www.instagram.com/reel/..."
              value={mediaUrl}
              onChange={(e) => { setMediaUrl(e.target.value); setUrlError(null);} }
              aria-invalid={!!urlError}
              aria-describedby={urlError ? "media-url-error" : undefined}
              className="mt-1 w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring"
            />
            <div className="mt-1 flex items-center gap-2">
              <button
                type="button"
                onClick={async()=>{
                  try { const clip = await navigator.clipboard.readText(); if (clip) setMediaUrl(clip); } catch {}
                }}
                className="text-xs rounded-full bg-white/10 px-2 py-1 border border-white/20 hover:bg-white/20"
              >Paste</button>
              {urlError && <span id="media-url-error" className="text-xs text-rose-500">{urlError}</span>}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Supported: Instagram, TikTok, Facebook. Some hosts limit frame access; we’ll fall back to captions/thumbnails.
            </p>
            {!isSupportedHost && mediaUrl && (
              <p className="mt-1 text-xs text-amber-600">This host isn’t fully supported yet—accuracy may be low.</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium">Caption (optional)</label>
              <input
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="e.g., ‘Comiendo rico en el norte de CDMX’"
                className="mt-1 w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring"
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Hashtags (optional)</label>
              <input
                value={hashtags}
                onChange={(e) => setHashtags(e.target.value)}
                placeholder="#cdmx #tepeyac"
                className="mt-1 w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium">City/Neighborhood Hint (optional)</label>
            <input
              value={cityHint}
              onChange={(e) => setCityHint(e.target.value)}
              placeholder="Roma Norte, Ciudad de México"
              className="mt-1 w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring"
            />
            <p className="text-xs text-gray-500 mt-1">Adds geo-bias; improves accuracy and cost.</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={loading}
              className={classNames(
                "rounded-lg px-4 py-2 text-white",
                loading ? "bg-gray-400" : "bg-black hover:bg-gray-800"
              )}
            >
              {loading ? "Finding…" : "Find Restaurant"}
            </button>

            {ran && (
              <button
                type="button"
                onClick={() => { setResults([]); setError(null); setRan(false); setMediaUrl(""); setCaption(""); setHashtags(""); setCityHint(""); setUrlError(null); formRef.current?.reset(); }}
                className="rounded-lg px-3 py-2 border hover:bg-gray-50"
              >
                Reset
              </button>
            )}
          </div>

          {error && (
            <div role="alert" className="mt-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}
        </form>

        {/* Results */}
        <div className="p-5 border-t" aria-busy={loading}>
          {loading && (
            <div className="mb-4"><StepBar step={step} /></div>
          )}
          <h3 className="text-sm font-medium text-gray-700">
            Results {ran && `(${results.length})`}
          </h3>

          {!ran && (
            <p className="mt-2 text-sm text-gray-500">
              We’ll show the top candidates here with confidence and reasoning.
            </p>
          )}

          {corsBlocked && (
            <div className="mt-3 rounded-lg border border-orange-200 bg-orange-50 text-orange-900 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium mb-1">Video frames blocked by platform</div>
                  <div className="text-sm mb-3">
                    This platform restricts frame access for analysis. We can only analyze the caption and metadata.
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      // For now, just show a hint. In a future version, could enable upload
                      setCorsBlocked(false);
                      setError("Try adding more details in the caption field, or use a platform that allows frame analysis.");
                    }}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-orange-100 hover:bg-orange-200 text-orange-800 rounded-lg transition-colors"
                  >
                    <Upload className="w-4 h-4" />
                    Add more details instead
                  </button>
                </div>
              </div>
            </div>
          )}

          {ran && results.length === 0 && !loading && !corsBlocked && (
            <div className="mt-3 rounded-lg border bg-yellow-50 text-yellow-900 p-3 text-sm">
              <div className="font-medium mb-1">No confident match found</div>
              <div className="text-xs">
                Try a clearer link (visible signage), add a city hint, or include the restaurant name in the caption.
              </div>
            </div>
          )}

          {results.length > 0 && (
            <>
              {/* Map Section */}
              <div className="mt-4 border-t pt-4">
                <button
                  type="button"
                  onClick={() => setMapCollapsed(!mapCollapsed)}
                  className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 mb-3 md:hidden"
                  aria-expanded={!mapCollapsed}
                >
                  <MapPin className="w-4 h-4" />
                  Map View
                  {mapCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                </button>
                
                <div className={`${mapCollapsed ? 'hidden' : 'block'} md:block`}>
                  {mapReady ? (
                    <div className="h-64 md:h-80 rounded-xl overflow-hidden border">
                      <GoogleMap
                        mapContainerStyle={{ width: '100%', height: '100%' }}
                        center={mapCenter}
                        zoom={mapZoom}
                        options={{ 
                          disableDefaultUI: false,
                          zoomControl: true,
                          streetViewControl: false,
                          fullscreenControl: false
                        }}
                      >
                        {markers.map((marker, idx) => (
                          <Marker
                            key={marker.result.placeId}
                            position={marker.position}
                            onClick={() => handleMarkerClick(marker.result)}
                            label={{
                              text: String(results.findIndex(r => r.placeId === marker.result.placeId) + 1),
                              color: 'white',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}
                            icon={
                              selectedResultIdx === results.findIndex(r => r.placeId === marker.result.placeId)
                                ? {
                                    path: google.maps.SymbolPath.CIRCLE,
                                    fillColor: '#10b981',
                                    fillOpacity: 1,
                                    strokeColor: '#ffffff',
                                    strokeWeight: 2,
                                    scale: 10
                                  }
                                : undefined
                            }
                          />
                        ))}
                      </GoogleMap>
                    </div>
                  ) : (
                    <div className="h-32 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center text-gray-500 text-sm">
                      Loading map...
                    </div>
                  )}
                </div>
              </div>

              {/* Results List */}
              <ul className="mt-4 space-y-4">
                {results.map((r, idx) => {
                  const isTop = idx === 0;
                  const isSelected = selectedResultIdx === idx;
                  return (
                    <li
                      key={r.placeId}
                      tabIndex={0}
                      role="group"
                      onClick={() => handleResultClick(idx)}
                      className={classNames(
                        "rounded-xl border p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 cursor-pointer transition-colors",
                        isTop ? "border-green-300 bg-green-50" : "border-gray-200 bg-white",
                        isSelected && !isTop ? "border-emerald-300 bg-emerald-50" : ""
                      )}
                    >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex gap-3">
                        <div className={classNames(
                          "flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                          isSelected ? "bg-emerald-500 text-white" : "bg-gray-200 text-gray-700"
                        )}>
                          {idx + 1}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold">{r.name}</h4>
                            {isTop && (
                              <span className="text-xs rounded-full bg-green-600 text-white px-2 py-0.5">Top match</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{r.address}</p>
                          
                          {/* Enriched data: rating, cuisine, hours */}
                          <div className="mt-2 space-y-1">
                            {r.rating && (
                              <div className="flex items-center">
                                <StarRating value={r.rating} size="sm" showNumber />
                              </div>
                            )}
                            
                            <div className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
                              {r.cuisine && (
                                <span className="px-2 py-0.5 bg-gray-100 rounded-md text-xs">{r.cuisine}</span>
                              )}
                              {typeof r.priceLevel === 'number' && (
                                <span className="text-xs text-gray-500">
                                  {'$'.repeat(Math.max(1, Math.min(4, r.priceLevel + 1)))}
                                </span>
                              )}
                              {typeof r.openNow === 'boolean' && (
                                <span className={`text-xs ${r.openNow ? 'text-green-600' : 'text-red-600'}`}>
                                  {r.openNow ? 'Open now' : 'Closed'}
                                </span>
                              )}
                            </div>
                            
                            {r.hours && r.hours.length > 0 && (
                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                <Clock className="w-3 h-3" />
                                <span className="line-clamp-1">{r.hours[new Date().getDay()]}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="min-w-[160px] text-right">
                        <div className="text-xs text-gray-600">Confidence</div>
                        {isTop ? (
                          <ConfidencePill score={r.confidence} />
                        ) : (
                          <div
                            role="meter"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={Math.round(Math.max(0, Math.min(1, r.confidence)) * 100)}
                            aria-valuetext={`Confidence ${percent(r.confidence)}`}
                            className="w-24 h-1.5 bg-gray-200 rounded overflow-hidden"
                          >
                            <div className="h-1.5 bg-emerald-400" style={{ width: percent(r.confidence) }} />
                          </div>
                        )}
                        <div className="mt-2 flex items-center justify-end gap-2">
                          <a
                            className="text-xs underline hover:no-underline"
                            href={`https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(r.placeId)}`}
                            target="_blank" rel="noreferrer"
                          >
                            Directions
                          </a>
                          {r.phone && (
                            <a href={`tel:${toTel(r.phone)}`} className="text-xs underline hover:no-underline">Call</a>
                          )}
                          <button
                            type="button"
                            onClick={() => navigator.clipboard?.writeText(r.placeId)}
                            className="text-xs underline hover:no-underline"
                          >
                            Copy ID
                          </button>
                        </div>
                      </div>
                    </div>

                    {r.reasons?.length > 0 && (
                      <div className="mt-3">
                        <EvidenceChips tokens={r.reasons} />
                      </div>
                    )}
                    </li>
                  );
                })}
              </ul>
              </>
            )}
        </div>

        <AttributionFooter />
      </div>

      {/* SR-only live region for status updates */}
      <div aria-live="polite" className="sr-only">
        {loading ? "Finding restaurant…" : ran ? "Results ready." : ""}
      </div>
    </div>
  );
}
